import React from "react";
import { Button } from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";

const RCC_SOURCE_KEY = "skygeni-rcc-source";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(1),
    textTransform: "none",
    fontWeight: 500,
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: "rgba(38, 97, 173, 0.08)",
    },
  },
}));

const NAV_MAP = {
  "Enhanced Insights": "enhancedPage",
  "Rep Performance": "repPage",
  "Other Insights": "othersPage",
  "Marketing Support Requests": "marketPage",
  "Growth Modeling": "growthModelingPage",
  "Pipeline Build & Sufficiency": "pipelineBuildandSuficencyPage",
  "Pipeline Velocity & Conversion": "pipelineVelocityandConversionPage",
  "Market Intel & Growth Levers": "marketIntelandGrowthLeversPage",
  "Sales Productivity": "salesProductivityPage",
  "Growth Science Insights": "growthScienceInsightsPage",
  "Custom Scorecards": "personalizedScorecardsPage",
  Settings: "SettingsPage",
  "Product Performance": "productPage",
};

/**
 * Check if the user navigated from Revenue Command Center to the current page
 * @returns {boolean}
 */
export const isFromRcc = () => {
  try {
    const sourceData = sessionStorage.getItem(RCC_SOURCE_KEY);
    if (!sourceData) return false;

    // Parse stored source data
    const { group, dashboard } = JSON.parse(sourceData);

    // Get current page state
    const currentGroup = localStorage.getItem("skygeni-user-page");

    // Check if we are in the correct group
    if (!currentGroup || currentGroup !== group) return false;

    // Check if we are on the correct dashboard within the group
    const dashboardKey = NAV_MAP[currentGroup];
    const currentDashboard = localStorage.getItem(
      `skygeni-user-${dashboardKey}`,
    );
    return currentDashboard === dashboard;
  } catch (e) {
    // If parsing fails or data is invalid, assume not from RCC
    return false;
  }
};

/**
 * Clear the RCC source marker
 */
export const clearRccSource = () => {
  sessionStorage.removeItem(RCC_SOURCE_KEY);
};

/**
 * Back to Revenue Command Center button component.
 * Only renders if user came from RCC (sessionStorage marker exists).
 */
const BackToRcc = () => {
  const classes = useStyles();
  const history = useHistory();

  const shouldShow = isFromRcc();
  if (!shouldShow) {
    if (sessionStorage.getItem(RCC_SOURCE_KEY)) {
      clearRccSource();
    }
    return null;
  }

  const handleBackToRcc = () => {
    // Clear the marker first
    clearRccSource();

    // Navigate to RCC using same pattern as handleGlobalRedirect in RccCard.jsx
    localStorage.setItem("skygeni-user-page", "Other Insights");
    localStorage.setItem("skygeni-user-othersPage", "Revenue Command Center");

    // Remove other page storage keys (following existing pattern from App.js changePage)
    localStorage.removeItem("skygeni-user-enhancedPage");
    localStorage.removeItem("skygeni-user-repPage");
    localStorage.removeItem("skygeni-user-SettingsPage");
    localStorage.removeItem("skygeni-user-productPage");
    localStorage.removeItem("skygeni-user-customerSuccessPage");
    localStorage.removeItem("skygeni-user-quotaGenerationPage");
    localStorage.removeItem("skygeni-user-pipelineProgressionPage");
    localStorage.removeItem("skygeni-user-pipelineVelocityandConversionPage");
    localStorage.removeItem("skygeni-user-pipelineBuildandSuficencyPage");
    localStorage.removeItem("skygeni-user-marketIntelandGrowthLeversPage");
    localStorage.removeItem("skygeni-user-salesProductivityPage");
    localStorage.removeItem("skygeni-user-growthScienceInsightsPage");
    localStorage.removeItem("skygeni-user-growthModelingPage");
    localStorage.removeItem("skygeni-user-personalizedScorecardsPage");
    localStorage.removeItem("skygeni-user-marketPage");

    // Navigate and reload (same pattern as RccCard)
    history.push("/");
    window.location.reload();
  };

  return (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={handleBackToRcc}
      className={classes.backButton}
      data-test="back-to-rcc-button"
    >
      Back to Revenue Command Center
    </Button>
  );
};

export default BackToRcc;
